import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VoyahFreeHeroEditor = () => {
  const [form, setForm] = useState({
    titleEn: "",
    titleAr: "",
    titleKu: "",
    subtitleEn: "",
    subtitleAr: "",
    subtitleKu: "",
    buttonEn: "",
    buttonAr: "",
    buttonKu: "",
    priceEn: "",
    priceAr: "",
    priceKu: "",
    linkTextEn: "",
    linkTextAr: "",
    linkTextKu: "",
    linkUrl: "",
  });

  const [background, setBackground] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // ================================
  // FETCH DATA IN 3 LANGUAGES
  // ================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VoyahFreeHeroSection?lang=en`),
          axios.get(`${API_BASE}/api/VoyahFreeHeroSection?lang=ar`),
          axios.get(`${API_BASE}/api/VoyahFreeHeroSection?lang=ku`),
        ]);

        setForm({
          titleEn: en.data.title,
          subtitleEn: en.data.subtitle,
          buttonEn: en.data.button,
          priceEn: en.data.price,
          linkTextEn: en.data.linkText,
          linkUrl: en.data.linkUrl,

          titleAr: ar.data.title,
          subtitleAr: ar.data.subtitle,
          buttonAr: ar.data.button,
          priceAr: ar.data.price,
          linkTextAr: ar.data.linkText,

          titleKu: ku.data.title,
          subtitleKu: ku.data.subtitle,
          buttonKu: ku.data.button,
          priceKu: ku.data.price,
          linkTextKu: ku.data.linkText,
        });

        setBackground(en.data.backgroundImage);
        setLoading(false);
      } catch (err) {
        console.error("Error loading Free Hero section:", err);
      }
    };

    load();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ================================
  // SAVE TEXT CHANGES
  // ================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/VoyahFreeHeroSection/1`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Saved successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save.");
    }
  };

  // ================================
  // UPLOAD BACKGROUND IMAGE
  // ================================
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select an image.");

    const token = localStorage.getItem("token");
    const fd = new FormData();
    fd.append("BackgroundImage", uploadFile);

    try {
      await axios.post(`${API_BASE}/api/VoyahFreeHeroSection/1/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Background updated!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  if (loading) return <p>Loading...</p>;

  const LANGS = ["En", "Ar", "Ku"];

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah Free – Hero Section Editor</h2>

      {/* ================================
          TEXT SECTIONS (3 LANGUAGES)
      ================================== */}
      <div className="row">
        {LANGS.map((lang) => (
          <div className="col-lg-4 col-md-6 mb-4" key={lang}>
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="text-primary mb-3">{lang} Version</h5>

                <label className="form-label">Title</label>
                <input
                  className="form-control mb-2"
                  name={`title${lang}`}
                  value={form[`title${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Subtitle</label>
                <input
                  className="form-control mb-2"
                  name={`subtitle${lang}`}
                  value={form[`subtitle${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Button</label>
                <input
                  className="form-control mb-2"
                  name={`button${lang}`}
                  value={form[`button${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Price</label>
                <input
                  className="form-control mb-2"
                  name={`price${lang}`}
                  value={form[`price${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Link Text</label>
                <input
                  className="form-control"
                  name={`linkText${lang}`}
                  value={form[`linkText${lang}`]}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* LINK URL (one only) */}
      <div className="mb-4">
        <label className="form-label fw-bold">Link URL</label>
        <input
          className="form-control"
          name="linkUrl"
          value={form.linkUrl}
          onChange={handleChange}
        />
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Changes
        </button>
      </div>

      {/* ================================
          BACKGROUND IMAGE
      ================================== */}
      <h4 className="mb-3">Background Image</h4>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          {background ? (
            <img
              src={resolveUrl(background)}
              className="w-100 rounded mb-3"
              style={{ maxHeight: "280px", objectFit: "cover" }}
              alt=""
            />
          ) : (
            <p>No background image uploaded.</p>
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload / Replace Background
          </button>
        </div>
      </div>

      {/* ================================
          MODAL
      ================================== */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Background Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VoyahFreeHeroEditor;
